// traceDerivedFileOps.js — file operations DERIVED from shell and script
// content, against the belief store.
//
// The file X-ray classifies by input shape, so it only sees edits made
// through file tools. Agents also edit through the shell — `sed -i` states a
// transform, `cat > f <<EOF` states new content, `echo >> f` states an
// append, a Python heredoc writes a literal — and today those claim (at
// best) as content-less shell writes. This module derives REAL file ops from
// them:
//
//   cat/tee heredoc into a redirect target  → write op, content = the body
//   echo/printf append (>>)                 → edit op, one append hunk
//   python heredoc with a literal write     → write op, content = the literal
//   sed -i s/…/…/ against a believed file   → edit op with real hunks, by
//                                             applying the transform to the
//                                             belief-store content
//
// Epistemics ride on every op: `derived: true` plus a `basis` naming how the
// content was obtained. Heredoc/echo/python content is QUOTED from the
// command's own bytes; only the sed replay leans on believed state, and it
// derives nothing unless the belief is complete (see traceBeliefStore) and
// the sed script sits inside a conservatively-supported subset — a transform
// we half-understand must not state hunks.

import { shellCommandOf, stripHeredocs, splitStatements, unquoted } from "./traceForgeOps.js";
import { tokenize } from "../../xray-factory/src/mine/shell.js";
import { hunkDelta } from "./traceFileTree.js";
import { buildBeliefStore } from "./traceBeliefStore.js";

// Bound per-command derivation fan-out, same rationale as the sibling caps.
const OPS_PER_COMMAND_CAP = 8;
const CONTENT_CAP = 200_000;

const HD_RE = /\bHD(\d+)\b/;

function str(v) {
  return typeof v === "string" && v ? v : null;
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content.map((b) => (typeof b === "string" ? b : b?.text || "")).join("\n");
}

// A redirect target token, unquoted. Rejects targets carrying live shell
// (variables, substitutions) — a derived op must name a real path.
function cleanPath(tok) {
  const t = String(tok || "").replace(/^["']|["']$/g, "");
  if (!t || /[$`*?{}<>|]/.test(t) || t === "-" || t.startsWith("/dev/")) return null;
  return t;
}

// ── sed subset ───────────────────────────────────────────────────────────────
// One s/pat/repl/ script, any single-char delimiter, optional `g`. The
// pattern may use . * ^ $ and literal text (BRE's literal (){}+?| included);
// backslash escapes other than \. \* \/ (or the delimiter) reject, character
// classes reject, backreferences reject. Everything rejected simply derives
// nothing.

function parseSedScript(script) {
  const s = String(script || "");
  if (s.length < 4 || s[0] !== "s") return null;
  const d = s[1];
  if (/[\w\s\\]/.test(d)) return null;
  const parts = [];
  let cur = "";
  for (let i = 2; i < s.length; i++) {
    const c = s[i];
    if (c === "\\" && s[i + 1] === d) { cur += d; i += 1; continue; }
    if (c === "\\") { cur += c + (s[i + 1] ?? ""); i += 1; continue; }
    if (c === d) { parts.push(cur); cur = ""; continue; }
    cur += c;
  }
  parts.push(cur);
  if (parts.length !== 3) return null;
  const [pat, repl, flags] = parts;
  if (!pat || !/^g?$/.test(flags)) return null;
  return { pat, repl, global: flags === "g" };
}

function sedPatternToRegex(pat, global) {
  let out = "";
  for (let i = 0; i < pat.length; i++) {
    const c = pat[i];
    if (c === "\\") {
      const n = pat[i + 1];
      if (n === "." || n === "*" || n === "/") { out += "\\" + n; i += 1; continue; }
      return null; // BRE groups, classes, backrefs — out of the subset
    }
    if (c === "[") return null;
    if ("(){}+?|".includes(c)) { out += "\\" + c; continue; }
    out += c; // . * ^ $ keep their regex meaning, everything else is itself
  }
  try {
    return new RegExp(out, global ? "g" : "");
  } catch {
    return null;
  }
}

function sedReplacement(repl) {
  let out = "";
  for (let i = 0; i < repl.length; i++) {
    const c = repl[i];
    if (c === "\\") {
      const n = repl[i + 1];
      if (n === "&") { out += "&"; i += 1; continue; }
      if (n === "\\") { out += "\\"; i += 1; continue; }
      return null; // \1 backrefs and escapes we don't model
    }
    if (c === "&") { out += "$&"; continue; }
    if (c === "$") { out += "$$"; continue; }
    out += c;
  }
  return out;
}

// Apply the transform to believed content; contiguous changed-line runs
// become old/new hunks. Null when nothing changed — a no-op transform is a
// read of sorts, not an edit.
function sedHunks(content, re, replacement) {
  const oldLines = content.split("\n");
  const newLines = oldLines.map((l) => l.replace(re, replacement));
  const hunks = [];
  let run = null;
  for (let i = 0; i < oldLines.length; i++) {
    if (oldLines[i] !== newLines[i]) {
      if (!run) run = { old: [], new: [] };
      run.old.push(oldLines[i]);
      run.new.push(newLines[i]);
    } else if (run) {
      hunks.push({ old: run.old.join("\n"), new: run.new.join("\n") });
      run = null;
    }
  }
  if (run) hunks.push({ old: run.old.join("\n"), new: run.new.join("\n") });
  return hunks.length ? hunks : null;
}

// ── python literal writes ────────────────────────────────────────────────────
// Chained `open("p", "w").write(<literal>)` / `pathlib .write_text(<literal>)`
// inside a python heredoc. Literal strings only — an f-string or a variable
// states content we don't hold.

function pyString(s, i) {
  const triple = s.slice(i, i + 3);
  const q = s[i];
  if (q !== '"' && q !== "'") return null;
  const isTriple = triple === q.repeat(3);
  const quote = isTriple ? triple : q;
  let j = i + quote.length;
  let out = "";
  while (j < s.length) {
    if (s[j] === "\\") {
      const e = s[j + 1];
      out += e === "n" ? "\n" : e === "t" ? "\t" : e === "\\" ? "\\" : e === "'" ? "'" : e === '"' ? '"' : e;
      j += 2;
      continue;
    }
    if (s.startsWith(quote, j)) return { value: out, end: j + quote.length };
    if (!isTriple && s[j] === "\n") return null;
    out += s[j];
    j += 1;
  }
  return null;
}

function pythonLiteralWrites(body) {
  const out = [];
  const openRe = /\bopen\(\s*(['"])([^'"\n]+)\1\s*,\s*(['"])([wa])\3[^)]*\)\s*\.write\(\s*/g;
  let m;
  while ((m = openRe.exec(body))) {
    const lit = pyString(body, m.index + m[0].length);
    if (lit) out.push({ path: m[2], content: lit.value, append: m[4] === "a" });
  }
  const wtRe = /\bPath\(\s*(['"])([^'"\n]+)\1\s*\)\s*\.write_text\(\s*/g;
  while ((m = wtRe.exec(body))) {
    const lit = pyString(body, m.index + m[0].length);
    if (lit) out.push({ path: m[2], content: lit.value, append: false });
  }
  return out;
}

// ── echo payloads ────────────────────────────────────────────────────────────
// `echo 'line' >> f` — the payload is the statement minus the verb, flags,
// and the redirect clause, unquoted. Anything carrying live shell rejects.

function echoContent(stmt) {
  const m = /^echo\s+(.*?)\s*>>\s*\S+\s*$/.exec(stmt.trim());
  if (!m) return null;
  let payload = m[1].trim();
  let newline = true;
  const flags = /^(-[neE]+)\s+/.exec(payload);
  if (flags) {
    if (flags[1].includes("n")) newline = false;
    payload = payload.slice(flags[0].length);
  }
  if (/[$`\\]/.test(payload)) return null;
  const q = payload[0];
  if ((q === "'" || q === '"') && payload.endsWith(q) && payload.length >= 2) {
    payload = payload.slice(1, -1);
  } else if (/['"]/.test(payload)) {
    return null; // partial quoting — not worth modeling
  }
  return payload + (newline ? "\n" : "");
}

/**
 * Derived file ops for one trace. `fileOps` is extractFileOps' output for the
 * same turns (the belief substrate and the path spellings); ops key as
 * `<blockId>#d<i>` so they claim the shell call through the standard door.
 */
export function extractDerivedFileOps(turns, fileOps = []) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const belief = buildBeliefStore(turns, fileOps);
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id && !resultById.has(b.tool_use_id)) {
        resultById.set(b.tool_use_id, b);
      }
    }
  }

  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      const cmd = shellCommandOf(b);
      if (!cmd || !b.id) continue;
      const result = resultById.get(b.id);
      const ok = !result?.isError && !contentText(result?.content).includes("<tool_use_error>");
      const { text, bodies } = stripHeredocs(cmd);
      let n = 0;
      const push = (op) => {
        if (n >= OPS_PER_COMMAND_CAP) return;
        ops.push({ key: `${b.id}#d${n}`, ok, readLines: 0, derived: true, ...op });
        n += 1;
      };

      for (const stmt of splitStatements(text)) {
        const bare = unquoted(stmt);
        const hd = HD_RE.exec(stmt);
        const body = hd ? bodies[parseInt(hd[1], 10)] : null;

        // cat/tee heredoc → a whole-file write (or append) of quoted content.
        // Targets are read from the raw statement (a quoted path is blanked in
        // `bare`), but the redirect operator must be live shell — `bare` still
        // holding a ">" at the match position proves it wasn't inside quotes.
        if (body != null && /^(cat|tee)\b/.test(bare.trim())) {
          const redir = /(>>?)\s*("[^"\n]+"|'[^'\n]+'|[^\s;|&]+)/.exec(stmt);
          const redirOk = redir && bare[redir.index] === ">";
          const isTee = /^tee\b/.test(bare.trim());
          const teeM = isTee ? /^\s*tee\s+(-a\s+)?("[^"\n]+"|'[^'\n]+'|[^\s;|&]+)/.exec(stmt) : null;
          const append = Boolean((redirOk && redir[1] === ">>") || teeM?.[1]);
          const rawTok = redirOk ? redir[2] : teeM?.[2];
          const path = rawTok && !/^HD\d+$/.test(rawTok) ? cleanPath(rawTok) : null;
          if (path) {
            const content = body.slice(0, CONTENT_CAP);
            if (append) {
              const d = hunkDelta("", content);
              push({ kind: "edit", path, plus: d.plus, minus: 0, hunks: [{ old: "", new: content }], basis: "heredoc append" });
            } else {
              push({ kind: "write", path, plus: content === "" ? 0 : content.split("\n").length, minus: 0, content, contentTruncated: body.length > CONTENT_CAP, basis: "heredoc" });
            }
            continue;
          }
        }

        // python heredoc → literal writes stated inside the script.
        if (body != null && /(^|\s)(python3?|uv\s+run\s+python3?)\b/.test(bare)) {
          for (const w of pythonLiteralWrites(body)) {
            const path = cleanPath(w.path);
            if (!path) continue;
            if (w.append) {
              const d = hunkDelta("", w.content);
              push({ kind: "edit", path, plus: d.plus, minus: 0, hunks: [{ old: "", new: w.content }], basis: "script literal append" });
            } else {
              push({ kind: "write", path, plus: w.content === "" ? 0 : w.content.split("\n").length, minus: 0, content: w.content.slice(0, CONTENT_CAP), basis: "script literal" });
            }
          }
          continue;
        }

        // echo append → one append hunk of quoted content.
        if (/^echo\b/.test(bare.trim())) {
          const target = />>\s*("[^"\n]+"|'[^'\n]+'|[^\s;|&]+)/.exec(stmt);
          const targetOk = target && bare[target.index] === ">";
          const path = targetOk ? cleanPath(target[1]) : null;
          const content = path ? echoContent(stmt) : null;
          if (path && content != null) {
            const d = hunkDelta("", content);
            push({ kind: "edit", path, plus: d.plus, minus: 0, hunks: [{ old: "", new: content }], basis: "echo append" });
            continue;
          }
        }

        // sed -i → real hunks, by replaying the transform on believed content.
        if (/^sed\s/.test(bare.trim()) && /\s-i(\S*)?(\s|$)/.test(bare)) {
          const toks = tokenize(stmt.trim());
          let script = null;
          let broken = false;
          const paths = [];
          for (let i = 1; i < toks.length && !broken; i++) {
            const t = toks[i];
            if (t === "-i" || t.startsWith("-i")) continue;
            if (t === "-e") continue;
            if (t.startsWith("-")) { broken = true; break; } // flags we don't model
            if (script == null) script = t;
            else paths.push(t);
          }
          if (broken) script = null;
          const parsed = script ? parseSedScript(script) : null;
          const re = parsed ? sedPatternToRegex(parsed.pat, parsed.global) : null;
          const repl = parsed ? sedReplacement(parsed.repl) : null;
          if (parsed && re && repl != null) {
            for (const raw of paths) {
              const path = cleanPath(raw);
              if (!path) continue;
              const content = belief.contentBefore(path, b.id);
              if (content == null) continue;
              const hunks = sedHunks(content, re, repl);
              if (!hunks) continue;
              let plus = 0;
              let minus = 0;
              for (const h of hunks) {
                const d = hunkDelta(h.old, h.new);
                plus += d.plus;
                minus += d.minus;
              }
              push({ kind: "edit", path, plus, minus, hunks, basis: "sed replay", derivedFromBelief: true });
            }
          }
        }
      }
    }
  }
  return ops;
}
